import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiToggleLeft, FiToggleRight } = FiIcons;

const SettingsTab = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchFeatureFlags();
  }, []);

  const fetchFeatureFlags = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.from('feature_flags').select('*').order('feature_name');
      if (error) throw error;
      setFeatures(data || []);
    } catch (error) {
      console.error('Error fetching feature flags:', error);
      setError('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (featureName, currentStatus) => {
    // Optimistically update UI
    setFeatures(prevFeatures =>
      prevFeatures.map(f =>
        f.feature_name === featureName ? { ...f, is_enabled: !currentStatus } : f
      )
    );

    try {
      const { error } = await supabase
        .from('feature_flags')
        .update({ is_enabled: !currentStatus })
        .eq('feature_name', featureName);

      if (error) {
        // Revert UI on error
        setFeatures(prevFeatures =>
          prevFeatures.map(f =>
            f.feature_name === featureName ? { ...f, is_enabled: currentStatus } : f
          )
        );
        throw error;
      }
    } catch (error) {
      console.error('Error updating feature flag:', error);
      setError(`Failed to update ${featureName}.`);
    }
  };

  const formatFeatureName = (name) => {
    return name.replace(/_/g, ' ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Feature Flags</h2>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 space-y-4">
          {features.map(feature => (
            <motion.div
              key={feature.feature_name}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
            >
              <span className="text-gray-900 font-medium capitalize">{formatFeatureName(feature.feature_name)}</span>
              <button
                onClick={() => handleToggle(feature.feature_name, feature.is_enabled)}
                className="flex items-center space-x-2"
              >
                <span className={`text-sm font-medium ${feature.is_enabled ? 'text-green-600' : 'text-gray-500'}`}>
                  {feature.is_enabled ? 'Enabled' : 'Disabled'}
                </span>
                <SafeIcon
                  icon={feature.is_enabled ? FiToggleRight : FiToggleLeft}
                  className={`text-3xl transition-colors ${feature.is_enabled ? 'text-green-500' : 'text-gray-400'}`}
                />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;